import {
    AccordionContent,
    AccordionIcon,
    AccordionRoot,
    AccordionTrigger
} from "@/app/(site)/_components/Atoms/Accordion"
import AccordionToggleIcon from "@/app/(site)/_components/GCMolecules/AccordionElement/AccordionToggleIcon"
import Checkbox from "@/app/(site)/_components/Organisms/ProductGrids/Filter/Inputs/Checkbox"
import RadioOption from "@/app/(site)/_components/Organisms/ProductGrids/Filter/Inputs/RadioOption"
import { SIZE_FILTER_FIELD } from "@/app/(site)/_components/Organisms/ProductGrids/types/options"
import {
    useProductGridContext,
    useStaticGridContext
} from "@/app/(site)/contexts/product-grid/ProductGridContext"
import {
    type SearchFilter,
    type Values,
    type ValuesFilterMeta
} from "@/lib/depict-client/types/api/ProductListingRequestV3"
import { buildGeneralSizeBuckets } from "@/lib/facet-mappings"
import { clsx } from "@/src/utils/clsx"

type FilterItemsProps = {
    filter: SearchFilter
    initialOpen: boolean
}

export default function FilterItems({ filter, initialOpen }: FilterItemsProps) {
    const { filterState, setFilter, clearFilter } = useProductGridContext()
    const { facetMappings } = useStaticGridContext()

    const onToggleFilter = (field: string, op: SearchFilter["op"], value: string) => {
        const activeFilter = filterState.filters.find(f => f.field === field)
        const isCurrentlySelected = activeFilter?.data.includes(value) ?? false
        if (isCurrentlySelected) clearFilter(field, value)
        else setFilter(field, op, value)
    }

    const onSelectRadioFilter = (
        field: string,
        op: SearchFilter["op"],
        value: string
    ) => {
        const activeFilter = filterState.filters.find(f => f.field === field)
        const isCurrentlySelected = activeFilter?.data.includes(value) ?? false
        if (isCurrentlySelected) {
            clearFilter(field, value)
        } else {
            // Clear the existing selection first, then set the new one
            activeFilter?.data.forEach(existing => clearFilter(field, existing))
            setFilter(field, op, value)
        }
    }

    const meta = filter.meta as ValuesFilterMeta | undefined
    const renderType = meta?.type ?? "checkbox"
    const isSizeFilter = filter.field === SIZE_FILTER_FIELD
    type RenderItem = {
        item: Values
        originalIndex: number
        countOverride?: number
    }

    const items: RenderItem[] = isSizeFilter
        ? buildGeneralSizeBuckets(meta?.values, meta?.counts, facetMappings)
              .filter(({ count }) => count > 0)
              .map(({ generalSize, index, count }) => ({
                  item: generalSize as unknown as Values,
                  originalIndex: index,
                  countOverride: count
              }))
        : (meta?.values ?? []).map((item, originalIndex) => ({
              item: item as unknown as Values,
              originalIndex
          }))

    const activeFilter = filterState.filters.find(f => f.field === filter.field)
    const activeCount = activeFilter?.data.length ?? 0

    return (
        <AccordionRoot
            accordionId={`filter-${filter.field}`}
            accordionName={filter.meta?.group_title ?? filter.field}
            initalState={initialOpen}
            hydratedState={initialOpen}
            className={clsx(
                "relative -mr-px -mt-px border border-col-border-neutral border-r-transparent first:mt-0",
                "data-[open=false]:lg:hover:z-10 data-[open=false]:lg:hover:border-col-border-subtle",
                "transition-[border-color] duration-200 ease-in-out"
            )}
        >
            <AccordionTrigger className="flex h-58 w-full items-center justify-between py-8 pl-12 pr-0 lg:pl-24 lg:pr-12">
                <div className="flex items-start gap-6 pt-3">
                    <span className="pt-1 Trim_Mono_POC_Text_14_130_-5_500_uppercase">
                        {filter.meta?.group_title}
                    </span>
                    {activeCount > 0 && (
                        <span className="Trim_Mono_POC_Text_14_130_-5_500_uppercase">
                            ({activeCount})
                        </span>
                    )}
                </div>
                <AccordionIcon
                    openIcon={<AccordionToggleIcon isOpen={true} />}
                    closeIcon={<AccordionToggleIcon isOpen={false} />}
                    className="flex h-44 w-44 items-center justify-center"
                />
            </AccordionTrigger>
            <AccordionContent>
                <div className="flex flex-col px-12 pb-40 pt-4 lg:px-24">
                    {items.map(({ item, originalIndex, countOverride }) => {
                        const isSelected =
                            activeFilter?.data.includes(String(item)) ?? false
                        const key = `${item}-${originalIndex}`
                        switch (renderType) {
                            case "checkbox-color":
                                return (
                                    <Checkbox
                                        key={key}
                                        valueIndex={originalIndex}
                                        isSizeFilter={false}
                                        showColorSwatch
                                        filter={filter}
                                        item={item}
                                        field={filter.field}
                                        op={filter.op}
                                        isSelected={isSelected}
                                        onAddFilter={onToggleFilter}
                                    />
                                )
                            case "radio":
                                return (
                                    <RadioOption
                                        key={key}
                                        valueIndex={originalIndex}
                                        filter={filter}
                                        item={item}
                                        field={filter.field}
                                        op={filter.op}
                                        isSelected={isSelected}
                                        onAddFilter={onSelectRadioFilter}
                                    />
                                )
                            case "checkbox":
                            case "checkbox-grid":
                            default:
                                return (
                                    <Checkbox
                                        key={key}
                                        valueIndex={originalIndex}
                                        isSizeFilter={isSizeFilter}
                                        filter={filter}
                                        item={item}
                                        field={filter.field}
                                        op={filter.op}
                                        isSelected={isSelected}
                                        countOverride={countOverride}
                                        onAddFilter={onToggleFilter}
                                    />
                                )
                        }
                    })}
                </div>
            </AccordionContent>
        </AccordionRoot>
    )
}
